"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import AuthLayout from "./templates/AuthLayout";
import RegisterEmailStep from "./molecules/RegisterEmailStep";
import RegisterPhoneStep from "./molecules/RegisterPhoneStep";
import RegisterNoAltruAccountStep from "./molecules/RegisterNoAltruAccountStep";
import RegisterAltruPendingStep from "./molecules/RegisterAltruPendingStep";
import RegisterCreatePasswordStep from "./molecules/RegisterCreatePasswordStep";
import RegisterConfirmingStep from "./molecules/RegisterConfirmingStep";
import RegisterNameStep from "./molecules/RegisterNameStep";
import type { RegisterStep } from "./types";
import { validateEmailExists } from "@/lib/auth/validateEmailExists";
import {
  validateAltruExists,
  validateAltruExistsByName,
  countAltruConstituentsByEmail,
} from "@/lib/auth/validateAltruExists";
import { saveSession } from "@/lib/auth/client";
import type { ApiResponse } from "@/lib/api/types";
import type { SessionData } from "@/lib/auth/client";

const AMBIGUOUS_ACCOUNT_ERROR =
  "We were unable to uniquely identify your account. Please contact tickets@groundsforsculpture.org to update your information.";

interface RegisterResponseData {
  session: {
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
  } | null;
  user: { id: string; email: string };
  profile: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    altruConstituentId: string | null;
  };
}

async function createAccount(
  email: string,
  phone: string,
  password: string,
  firstName: string,
  lastName: string,
): Promise<SessionData | null> {
  const res = await fetch("/api/auth/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      password,
      confirmPassword: password,
      firstName,
      lastName,
      phoneNumber: phone,
    }),
  });

  const json = (await res.json()) as ApiResponse<RegisterResponseData>;
  if (!res.ok || !json.success || !json.data) {
    throw new Error(json.error?.message ?? "Unable to create account");
  }

  const { session, user, profile } = json.data;
  if (!session) return null;

  return {
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    expiresAt: session.expiresAt,
    user,
    profile,
  };
}

/**
 * @component Register
 * @description Full account registration flow:
 * 1. email — validate uniqueness in Supabase + Altru count check
 * 2. phone — collect phone number and check Altru existence (when count > 1)
 * 3. no-altru-account — if no Altru record, prompt user to create a ticketing account first
 *    altru-pending — if returning from Altru but record not yet searchable, show retry screen
 *    create-password — if Altru record exists, go straight to password creation
 * 4. confirming — spinner while account is created, then redirects to /dashboard
 * @returns The registration flow wrapped in the shared auth layout.
 */
export default function Register() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const fromAltru = searchParams.get("from") === "altru";

  const [step, setStep] = useState<RegisterStep>("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [existsInAltru, setExistsInAltru] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * Resolves the Altru constituent count for an email, applying the
   * from=altru retry strategy when count is 0:
   * - First attempt: wait 2s and retry once
   * - If still 0 after retry: show altru-pending screen instead of no-altru-account
   */
  const resolveAltruCount = async (emailToCheck: string, isFromAltru: boolean): Promise<number> => {
    const count = await countAltruConstituentsByEmail(emailToCheck);

    if (count === 0 && isFromAltru) {
      // New ticketing account may not be indexed yet — wait and retry once
      await new Promise((resolve) => setTimeout(resolve, 500));
      const retryCount = await countAltruConstituentsByEmail(emailToCheck);
      if (retryCount === 0) {
        // Still not found — show pending screen, not no-altru-account
        setStep("altru-pending");
        return -1; // sentinel: caller should bail
      }
      return retryCount;
    }

    return count;
  };

  const handleEmailSubmit = async (submittedEmail: string, forceFromAltru = false) => {
    setEmail(submittedEmail);
    setLoading(true);
    setError(null);
    const isFromAltru = fromAltru || forceFromAltru;
    try {
      // Always check Supabase — unless returning from Altru (account can't exist yet)
      if (!isFromAltru) {
        const exists = await validateEmailExists(submittedEmail);
        if (exists) {
          setError("An account with this email already exists. Please log in.");
          return;
        }
      }

      // Check Altru constituent count:
      // 0 → no constituent (or pending — handled inside resolveAltruCount)
      // 1 → unambiguous match, go straight to create-password
      // 2+ → ambiguous, require phone to narrow down
      const count = await resolveAltruCount(submittedEmail, isFromAltru);
      if (count === -1) return; // altru-pending step already set

      if (count === 0) {
        setExistsInAltru(false);
        setStep("no-altru-account");
      } else if (count === 1) {
        setExistsInAltru(true);
        setStep("create-password");
      } else {
        setExistsInAltru(false);
        setStep("phone");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAltruPendingRetry = async () => {
    setLoading(true);
    setError(null);
    try {
      const count = await countAltruConstituentsByEmail(email);

      if (count === 0) {
        setError(
          "Your account is still not available. Please wait a little longer and try again, or contact support.",
        );
      } else if (count === 1) {
        setExistsInAltru(true);
        setStep("create-password");
      } else {
        setExistsInAltru(false);
        setStep("phone");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (submittedPhone: string) => {
    setPhone(submittedPhone);
    setLoading(true);
    setError(null);
    try {
      const result = await validateAltruExists(email, submittedPhone);
      if (result === "found") {
        setExistsInAltru(true);
        setStep("create-password");
      } else if (result === "ambiguous") {
        // Multiple different people share this email + phone — ask for name to narrow down
        setStep("name");
      } else {
        setExistsInAltru(false);
        setStep("no-altru-account");
      }
    } catch {
      setError("Unable to validate account details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = async (submittedFirstName: string, submittedLastName: string) => {
    setFirstName(submittedFirstName);
    setLastName(submittedLastName);
    setLoading(true);
    setError(null);
    try {
      const result = await validateAltruExistsByName(email, phone, submittedFirstName, submittedLastName);
      if (result === "found") {
        setExistsInAltru(true);
        setStep("create-password");
      } else if (result === "not_found") {
        setExistsInAltru(false);
        setStep("no-altru-account");
      } else {
        // Still ambiguous after name — requires GFS manual cleanup
        setError(AMBIGUOUS_ACCOUNT_ERROR);
      }
    } catch {
      setError("Unable to validate account details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (password: string) => {
    setLoading(true);
    setError(null);
    setStep("confirming");
    try {
      const session = await createAccount(email, phone, password, firstName, lastName);
      if (session) {
        saveSession(session, false);
      }
      router.replace("/dashboard");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "We ran into an error. Please try again or contact support.";

      setError(
        message.includes("unable to uniquely identify your account")
          ? AMBIGUOUS_ACCOUNT_ERROR
          : message,
      );
      setStep(existsInAltru ? "create-password" : "no-altru-account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      {step === "email" && (
        <RegisterEmailStep
          onContinue={handleEmailSubmit}
          loading={loading}
          error={error}
        />
      )}

      {step === "phone" && (
        <RegisterPhoneStep
          email={email}
          onEditEmail={() => {
            setError(null);
            setStep("email");
          }}
          onContinue={handlePhoneSubmit}
          loading={loading}
          error={error}
        />
      )}

      {step === "name" && (
        <RegisterNameStep
          email={email}
          onEditEmail={() => {
            setError(null);
            setStep("email");
          }}
          onContinue={handleNameSubmit}
          loading={loading}
          error={error}
        />
      )}

      {step === "no-altru-account" && (
        <RegisterNoAltruAccountStep
          onContinue={() => handleEmailSubmit(email, true)}
          loading={loading}
        />
      )}

      {step === "altru-pending" && (
        <RegisterAltruPendingStep
          onRetry={handleAltruPendingRetry}
          email={email}
          onEditEmail={() => { setError(null); setStep("email"); }}
          loading={loading}
          error={error}
        />
      )}

      {step === "create-password" && (
        <RegisterCreatePasswordStep
          email={email}
          onEditEmail={() => {
            setError(null);
            setStep("email");
          }}
          onContinue={handlePasswordSubmit}
          loading={loading}
          error={error}
        />
      )}

      {step === "confirming" && <RegisterConfirmingStep />}
    </AuthLayout>
  );
}
